import React, { useEffect, useMemo, useState } from 'react'
import AdminLayout from '../../../layouts/AdminLayout'
import { Accordion, Tab, Tabs, useAccordionButton } from 'react-bootstrap'
import { IconAdjustmentsHorizontal } from '@tabler/icons-react';
import RoomMaintenanceFilterComponent from '../components/RoomMaintenanceFilterComponent';
import RoomMaintenanceTableComponent from '../components/RoomMaintenanceTableComponent';
import CreateRoomMaintenanceDialog from '../components/CreateRoomMaintenanceDialog';
import { CreateRoomMaintenancePrefill } from '../components/CreateRoomMaintenanceComponent';
import UpdateRoomMaintenanceDialog from '../components/UpdateRoomMaintenanceDialog';
import RoomMaintenanceCalendar from '../components/RoomMaintenanceCalendar';
import JumpToDateRangeControl from '../../pms-calendar/components/JumpToDateRangeControl';
import { EventsPayload } from '../../pms-calendar/lib/EventsPayload';
import mapBookingEventCalendar from '../../pms-calendar/utils/mapBookingEventCalendar';
import useIndexRoomMaintenance from '../../../services/room-maintenance/hooks/useIndexRoomMaintenance';
import MapRoomCalendar from '../../pms-calendar/utils/MapRoomCalendar';
import { mapOccCalendar } from '../../pms-calendar/utils/mapOccCalendar';
import { ResourcePayload } from '../../pms-calendar/lib/ResourcesPayload';
import useGetAllRoomType from '../../../services/room-type/hooks/useGetAllRoomType';
import useOccupancy from '../../../services/availability-and-rates/hooks/useOccupancy';
import { addDays, format, startOfDay } from 'date-fns';
import { OccPayload } from '../../pms-calendar/lib/OccPayload';
import useAvailability from '../../../services/availability-and-rates/hooks/useAvailability';
import useDataCalendar from '../../../services/availability-and-rates/hooks/useDataCalendar';
import FullPageError from '../../../shared/components/errors/FullPageError';
import FullPageSpinner from '../../../shared/components/spinner/FullPageSpinner';
import RequireAuth from '../../../auth/middleware/RequireAuth';
import { useAuth } from '../../../auth/context/AuthProvider';
import { RoomMaintenanceFilter } from '../schema/RoomMaintenanceFilterSchema';
import { dateRangesOverlap } from '../../../shared/utils/dateUtils';
import { toast } from 'sonner';
import { usePropertyReadOnly } from '../../../shared/hooks/usePropertyReadOnly';
import ReadOnlyGuard from '../../../shared/components/ReadOnlyGuard';
import PropertyInactiveBanner from '../../../shared/components/PropertyInactiveBanner';

function CustomToggle({ children, eventKey }: { children: React.ReactNode, eventKey: string }) {
    const decoratedOnClick = useAccordionButton(eventKey);

    return (
        <button className="btn btn-icon me-2" onClick={decoratedOnClick}>
            {children}
        </button>
    );
}

const RoomMaintenancePage: React.FC = () => {
    const { user } = useAuth();
    const { isReadOnly } = usePropertyReadOnly();
    const [activeTab, setActiveTab] = useState("calendar");

    const [showCreate, setShowCreate] = useState<boolean>(false)
    const [createPrefill, setCreatePrefill] = useState<CreateRoomMaintenancePrefill | null>(null)
    const [showUpdate, setShowUpdate] = useState<boolean>(false)
    const [idWillUpdate, setIdWillUpdate] = useState<string | null>(null)

    const handleOpenUpdate = (id: string) => {
        setIdWillUpdate(id)
        setShowUpdate(true)
    }

    // Dipakai khusus dari klik event di kalender (tabel Edit sudah dijaga oleh
    // RequireAuth yang membungkus Dropdown.Item-nya) — klik event tidak punya
    // pembungkus RequireAuth, jadi guard-nya wajib di sini.
    const handleEditFromCalendar = (id: string) => {
        const authorized = user?.role.includes('dev') || user?.permissions.includes('room_maintenance_update');
        if (!authorized) return;
        if (isReadOnly) return;
        handleOpenUpdate(id);
    }

    const handleCloseUpdate = () => {
        setIdWillUpdate(null)
        setShowUpdate(false)
    }

    const handleCloseCreate = () => {
        setShowCreate(false)
        setCreatePrefill(null)
    }

    // Drag-select di kalender → buka form Create Room Maintenance, pre-filled dari
    // tanggal+kamar yang di-drag. Pola sama seperti drag-create booking di
    // CalendarBooking.tsx (pms-calendar), tapi target-nya room maintenance (bukan
    // reservasi) karena itu memang tujuan halaman ini.
    const handleCreateFromDrag = (startDate: string, endDate: string, roomTypeId: string, roomId: string) => {
        const authorized = user?.role.includes('dev') || user?.permissions.includes('room_maintenance_create');
        if (!authorized) return;
        if (isReadOnly) return;
        if (!roomTypeId || !roomId) return;

        const checkinDate = format(new Date(startDate), 'yyyy-MM-dd');
        const checkoutDate = format(new Date(endDate), 'yyyy-MM-dd');

        // Room maintenance tidak boleh dibuat pada kamar yang sudah ada booking di
        // rentang yang sama — cek langsung terhadap data ghost booking yang sudah
        // di-fetch (lihat useDataCalendar di bawah), fail-fast sebelum form terbuka.
        const hasBookingConflict = (bookings?.data ?? []).some((booking) =>
            booking.room_id === roomId &&
            dateRangesOverlap(checkinDate, checkoutDate, booking.checkin_date, booking.checkout_date)
        );
        if (hasBookingConflict) {
            toast.error('This room already has a booking during the selected dates. Please move the booking first before creating a room maintenance block.', { id: 'room-maintenance-booking-conflict' });
            return;
        }

        setCreatePrefill({
            checkinDate,
            checkoutDate,
            roomTypeId,
            roomId,
        });
        setShowCreate(true)
    }

    const [todayDate, setTodayDate] = useState<string>('');
    const [date30DaysFromNow, setDate30DaysFromNow] = useState<string>('');

    useEffect(() => {
        const today = startOfDay(new Date());
        const date30Days = startOfDay(addDays(today, 30));
        const formattedToday = format(today, 'yyyy-MM-dd');
        const formatted30DaysFromNow = format(date30Days, 'yyyy-MM-dd');
        setTodayDate(formattedToday);
        setDate30DaysFromNow(formatted30DaysFromNow);
    }, []);

    // Filter kalender: SELALU dibatasi ke rentang tanggal yang sedang tampil
    // (todayDate..date30DaysFromNow) — dibutuhkan supaya event yang di-map ke
    // FullCalendar cuma sebesar rentang yang benar-benar dirender (ringan &
    // konsisten dengan navigasi ←/TODAY/→ / jump-to-date).
    const calendarDateFilters = useMemo(() => ({
        date: {
            gte: todayDate,
            lte: date30DaysFromNow,
        }
    }), [todayDate, date30DaysFromNow]);

    // Filter & pagination TABEL: independen dari kalender. Default: semua record
    // room-maintenance mulai HARI INI ke depan, TANPA batas atas — beda dari
    // kalender yang dibatasi 30 hari. Aman ditampilkan tanpa batas karena tabel
    // pakai pagination backend sungguhan (bukan `all:1`), jadi tetap ringan.
    const todayISO = useMemo(() => format(startOfDay(new Date()), 'yyyy-MM-dd'), []);
    const [tableFilters, setTableFilters] = useState<any>({ date: { gte: todayISO } });
    const [tablePage, setTablePage] = useState<number>(1);

    const handleFilterChange = (filterData: RoomMaintenanceFilter) => {
        const newFilters: any = {}; // Mulai dengan objek kosong

        // Handle room_type_id
        if (filterData.room_type_id && filterData.room_type_id !== 'all') {
            newFilters.room_type_id = filterData.room_type_id;
        }

        // Handle room_id
        if (filterData.room_id && filterData.room_id !== 'all') {
            newFilters.room_id = filterData.room_id;
        }

        // Handle date filter berdasarkan 'date_type'
        if (filterData.date_type && filterData.date_type !== 'all') {
            // date_type akan menjadi key (e.g., 'checkin_date', 'checkout_date')
            newFilters[filterData.date_type] = {
                gte: filterData.gte || undefined,
                lte: filterData.lte || undefined,
            };
        } else {
            // Jika 'date_type' adalah 'all', kembali ke default: hari ini ke depan,
            // tanpa batas atas.
            newFilters.date = {
                gte: todayISO,
            };
        }

        setTableFilters(newFilters);
        setTablePage(1);
    };

    const { data: occupancy, isLoading: isOccLoading, isError: isOccError, isSuccess: isOccSuccess } = useOccupancy({
        gte: todayDate,
        lte: date30DaysFromNow
    });

    const [occupancyPayload, setOccupancyPayload] = useState<OccPayload[]>([]);

    useEffect(() => {
        if (occupancy) {
            console.log('Occupancy ready to mapping');
            setOccupancyPayload(mapOccCalendar({ occupancy }));
        }
    }, [occupancy]);

    const { data: rooms, isLoading: isRoomsLoading, isError: isRoomsError, isSuccess: isRoomsSuccess } = useGetAllRoomType({
        all: true
    });

    const [resourcesPayload, setResourcesPayload] = useState<ResourcePayload[]>([]);
    const [showUnsigment, _setShowUnsigment] = useState<boolean>(false)
    const [showBaseOnRoomType, _setShowBaseOnRoomType] = useState<string>('')

    useEffect(() => {
        if (rooms) {
            console.log('Room types ready to mapping');
            setResourcesPayload(MapRoomCalendar({ roomTypes: rooms, isUnsigmentChecked: showUnsigment, showBaseOnRoomType }));
        }
    }, [rooms, showUnsigment, showBaseOnRoomType]);

    // Dipakai untuk render event di kalender — butuh SEMUA record dalam rentang
    // yang tampil (bukan terpaginasi), makanya tetap pakai `all: 1`.
    const { data: roomMaintenance, isLoading, isError } = useIndexRoomMaintenance({
        filters: calendarDateFilters,
        include: 'property,roomType,room',
        all: 1,
    });

    // Dipakai untuk tab Tabel — pagination backend sungguhan (TANPA `all: 1`),
    // filter independen dari kalender (lihat `tableFilters` di atas).
    const { data: roomMaintenanceTable, isLoading: isTableLoading, isError: isTableError, isFetching: isTableFetching } = useIndexRoomMaintenance({
        filters: tableFilters,
        include: 'property,roomType,room',
        page: tablePage,
        limit: 10,
    });

    const { data: availability, isLoading: isAviLoading, isError: isAviError, isSuccess: isAviSuccess } = useAvailability({
        gte: todayDate,
        lte: date30DaysFromNow
    });

    // Booking sungguhan, ditampilkan READ-ONLY ("ghost") di kalender Room Maintenance —
    // supaya staff bisa melihat kamar mana yang sudah ada tamunya sebelum memblokir
    // kamar itu untuk maintenance. Tidak memblokir render kalender kalau masih
    // loading/gagal — ghost booking bersifat pelengkap, bukan data inti.
    const { data: bookings } = useDataCalendar({
        gte: todayDate,
        lte: date30DaysFromNow,
        property_id: user?.active_property_id,
        query: 'booking',
    });

    const [eventsPayload, setEventsPayload] = useState<EventsPayload[]>([]);

    useEffect(() => {
        if (availability && roomMaintenance) {
            console.log('Bookings ready to mapping');
            setEventsPayload(mapBookingEventCalendar({
                booking: bookings ?? { success: true, message: '', code: 200, data: [] },
                availability: availability,
                unasigment: { success: true, message: '', code: 200, data: [] }, // Add appropriate unasigment data here
                isUnsigmentChecked: showUnsigment,
                showBaseOnRoomType: showBaseOnRoomType,
                roomMaintenance
            }));
        }
    }, [availability, roomMaintenance, bookings]);

    const isLoadingData = isAviLoading || isOccLoading || isRoomsLoading || isLoading;
    const isMappingEmpty = resourcesPayload.length === 0 || eventsPayload.length === 0 || occupancyPayload.length === 0 || isRoomsError || isAviError || isOccError || isError;

    const isDataReady =
        isRoomsSuccess &&
        isAviSuccess &&
        isOccSuccess &&
        resourcesPayload.length > 0 &&
        eventsPayload.length > 0 &&
        occupancyPayload.length > 0;

    return (
        <AdminLayout>
            <div className="container-fluid mt-5">
                <div className="table-container">
                    <PropertyInactiveBanner />
                    <div className='d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center gap-2 border-bottom pb-2'>
                        <Tabs
                            defaultActiveKey='table'
                            activeKey={activeTab}
                            onSelect={(k) => setActiveTab(k || "calendar")}
                            variant='pills'
                        >
                            <Tab eventKey='calendar' title='Calendar' tabClassName='custom-tab' />
                            <Tab eventKey='table' title='Table' tabClassName='custom-tab' />
                        </Tabs>
                        <div className='d-flex flex-wrap align-items-center gap-2'>
                            {activeTab === 'calendar' && (
                                <JumpToDateRangeControl
                                    defStartDate={todayDate}
                                    defEndDate={date30DaysFromNow}
                                    onChangeDate={(callback) => {
                                        setTodayDate(callback.start_date);
                                        setDate30DaysFromNow(callback.end_date);
                                    }}
                                />
                            )}
                            {/* Create button */}
                            <RequireAuth requiredRoles={['dev']} requiredPermissions={['room_maintenance_create']} asComponent>
                                <ReadOnlyGuard>
                                    <button className="btn btn-primary" onClick={() => setShowCreate(true)}>Create</button>
                                </ReadOnlyGuard>
                            </RequireAuth>
                        </div>
                    </div>
                    <div className='mt-3'>
                        {activeTab === 'calendar' && (
                            <>
                                {(isRoomsError || isAviError || isOccError) && <FullPageError />}
                                {(isLoadingData || isMappingEmpty) && <FullPageSpinner />}
                                {isDataReady && (
                                    <RoomMaintenanceCalendar
                                        todayDate={todayDate}
                                        date30DaysFromNow={date30DaysFromNow}
                                        setTodayDate={setTodayDate}
                                        setDate30DaysFromNow={setDate30DaysFromNow}
                                        onCreateFromDrag={handleCreateFromDrag}
                                        occupancyPayload={occupancyPayload}
                                        resourcesPayload={resourcesPayload}
                                        eventsPayload={eventsPayload}
                                        onEditRoomMaintenance={handleEditFromCalendar}
                                        isReadOnly={isReadOnly}
                                    />
                                )}
                            </>
                        )}
                        {activeTab === 'table' && (
                            <>
                                <Accordion defaultActiveKey="0" flush>
                                    <div className="search-container mb-3">
                                        <div className="d-flex">
                                            <CustomToggle eventKey="0">
                                                <IconAdjustmentsHorizontal className="text-secondary icon" />
                                            </CustomToggle>
                                        </div>
                                    </div>
                                    <Accordion.Collapse eventKey="0">
                                        <RoomMaintenanceFilterComponent onFilterSubmit={handleFilterChange} />
                                    </Accordion.Collapse>
                                </Accordion>
                                <RoomMaintenanceTableComponent
                                    data={roomMaintenanceTable} // Kirim seluruh objek response
                                    isLoading={isTableLoading || isTableFetching} // Gabungkan status loading
                                    isError={isTableError}
                                    onUpdate={handleOpenUpdate}
                                    onPageChange={(newPage) => setTablePage(newPage)} // Kirim fungsi untuk ganti halaman
                                />
                            </>
                        )}
                    </div>
                </div>
            </div>
            {showCreate && (
                <RequireAuth requiredRoles={['dev']} requiredPermissions={['room_maintenance_create']} asComponent>
                    <ReadOnlyGuard mode="hide">
                        <CreateRoomMaintenanceDialog
                            show={showCreate}
                            onClose={handleCloseCreate}
                            prefill={createPrefill}
                        />
                    </ReadOnlyGuard>
                </RequireAuth>
            )}
            {showUpdate && idWillUpdate && (
                <RequireAuth requiredRoles={['dev']} requiredPermissions={['room_maintenance_update']} asComponent>
                    <ReadOnlyGuard mode="hide">
                        <UpdateRoomMaintenanceDialog
                            show={showUpdate}
                            id={idWillUpdate}
                            onClose={handleCloseUpdate}
                        />
                    </ReadOnlyGuard>
                </RequireAuth>
            )}
        </AdminLayout>
    )
}

export default RoomMaintenancePage